import { useState } from 'react';
import { getAdminQuestions, saveQuestion, GetAdminQuestionsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

type Q = GetAdminQuestionsOutputType['questions'][0];

const blankQ = { question: '', optionA: '', optionB: '', optionC: '', optionD: '', correctAnswer: 'B' as const, questionType: 'Standard' as const };

export default function QuestionManager({ moduleId, questions, onRefresh }: { moduleId: string; questions: Q[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<Q | typeof blankQ | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const startNew = () => { setEditing({ ...blankQ }); setIsNew(true); };
  const startEdit = (q: Q) => { setEditing({ ...q }); setIsNew(false); };
  const cancel = () => { setEditing(null); setIsNew(false); };

  const save = async () => {
    if (!editing || !editing.question.trim()) { toast.error('Question text is required'); return; }
    setSaving(true);
    try {
      await saveQuestion({
        id: isNew ? undefined : (editing as Q).id,
        moduleId,
        question: editing.question,
        optionA: editing.optionA,
        optionB: editing.optionB,
        optionC: editing.optionC,
        optionD: editing.optionD,
        correctAnswer: editing.correctAnswer as 'A' | 'B' | 'C' | 'D',
        questionType: editing.questionType as 'Standard' | 'Scenario',
      });
      toast.success(isNew ? 'Question added' : 'Question updated');
      setEditing(null);
      onRefresh();
    } catch { toast.error('Failed to save question'); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await saveQuestion({ id, moduleId, delete: true });
      toast.success('Question deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const upd = (field: string, val: string) => setEditing(e => e ? { ...e, [field]: val } : e);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{questions.length} Questions</p>
        <Button size="sm" onClick={startNew} className="h-7 bg-primary text-primary-foreground px-3">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add question
        </Button>
      </div>

      {/* Add/Edit Form */}
      {editing && (
        <div className="border border-primary/30 bg-primary/5 p-4 mb-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{isNew ? 'New Question' : 'Edit Question'}</p>
          <Textarea placeholder="Question text..." value={editing.question} onChange={e => upd('question', e.target.value)} className="min-h-16" />
          <div className="grid grid-cols-2 gap-2">
            {(['A', 'B', 'C', 'D'] as const).map(opt => (
              <div key={opt} className="flex items-center gap-2">
                <span className="text-xs font-bold text-muted-foreground w-4">{opt}</span>
                <Input value={(editing as any)[`option${opt}`]} onChange={e => upd(`option${opt}`, e.target.value)} placeholder={`Option ${opt}`} className="h-8 text-sm" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Correct answer</label>
              <Select value={editing.correctAnswer} onValueChange={v => upd('correctAnswer', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['A', 'B', 'C', 'D'].map(o => <SelectItem key={o} value={o}>Option {o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Type</label>
              <Select value={editing.questionType} onValueChange={v => upd('questionType', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">Standard</SelectItem>
                  <SelectItem value="Scenario">Scenario</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground">{saving ? 'Saving...' : 'Save'}</Button>
            <Button size="sm" variant="outline" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Question List */}
      {questions.length === 0 && !editing && (
        <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border">No questions yet. Add some or use the Seed Content feature.</p>
      )}
      <div className="space-y-2">
        {questions.map((q, i) => (
          <div key={q.id} className="border border-border bg-card p-3 flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-muted-foreground">{i + 1}.</span>
                <span className={`text-xs px-1.5 py-0.5 ${q.questionType === 'Scenario' ? 'bg-accent/20 text-accent-foreground' : 'bg-muted text-muted-foreground'}`}>{q.questionType}</span>
              </div>
              <p className="text-sm text-foreground line-clamp-2">{q.question}</p>
              <p className="text-xs text-muted-foreground mt-1">Correct: <span className="text-primary font-medium">{q.correctAnswer}</span> — {(q as any)[`option${q.correctAnswer}`]}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(q)}><Pencil className="w-3 h-3" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete this question?</AlertDialogTitle></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(q.id)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
